import React, { useEffect, useMemo, useState } from 'react';

import { ErrorModal } from 'src/components';
import './validation-fail-modal.scss';
import { Environment } from 'src/Environment';

export enum ValidationFailModalStateType {
  HIDDEN,
  FAILED,
  FAILED_ONE_TRY_LEFT,
  FAILED_MAX_TRIES_EXCEEDED,
  FAILED_MAX_TRIES_EXCEEDED_FOR_RENTAL_ONLY,
  FAILED_WITH_RENTAL_PRODUCT,
  FAILED_ONE_TRY_LEFT_WITH_RENTAL_PRODUCT,
}

type HiddenState = {
  state: ValidationFailModalStateType.HIDDEN;
};

type Failed = {
  state: ValidationFailModalStateType.FAILED;
  onEditBankDetailsButtonClick: () => void;
  onUploadBankStatementButtonClick?: () => void;
};

type FailedOneTryLeftState = {
  state: ValidationFailModalStateType.FAILED_ONE_TRY_LEFT;
  onEditBankDetailsButtonClick: () => void;
  onUploadBankStatementButtonClick?: () => void;
};

type FailedMaxTriesExceeded = {
  state: ValidationFailModalStateType.FAILED_MAX_TRIES_EXCEEDED;
  maxNumberOfTriesWord: string;
  onRestartApplication?: () => void;
  onUploadBankStatementButtonClick?: () => void;
};

type FailedMaxTriesExceededForRentalOnly = {
  state: ValidationFailModalStateType.FAILED_MAX_TRIES_EXCEEDED_FOR_RENTAL_ONLY;
  onClose: () => void;
};

type FailedWithRentalProduct = {
  state: ValidationFailModalStateType.FAILED_WITH_RENTAL_PRODUCT;
  onTryAgain: () => void;
};

type FailedOneTryLeftWithRentalProduct = {
  state: ValidationFailModalStateType.FAILED_ONE_TRY_LEFT_WITH_RENTAL_PRODUCT;
  onTryAgain: () => void;
};

export type ValidationFailModalState = (
  | HiddenState
  | Failed
  | FailedOneTryLeftState
  | FailedMaxTriesExceeded
  | FailedMaxTriesExceededForRentalOnly
  | FailedWithRentalProduct
  | FailedOneTryLeftWithRentalProduct
) & { errorMessage?: string };

type ModalProps = {
  subtext?: string;
  errorMessage?: string;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose: () => void;
};

export const ValidationFailModal = (props: ValidationFailModalState) => {
  const [modalProps, setModalProps] = useState<ModalProps>({ onClose: () => {} });
  const bavIsDisabled = useMemo(() => Environment.current.disabledFeatures.bav, []);

  useEffect(() => {
    if (!bavIsDisabled) {
      switch (props.state) {
        case ValidationFailModalStateType.FAILED:
          setModalProps({
            subtext:
              "We couldn't verify your bank account details. Please check the information and try again.",
            errorMessage: props.errorMessage,
            confirmButtonLabel: 'Try again',
            cancelButtonLabel: 'Upload bank statement',
            onConfirm: props.onEditBankDetailsButtonClick,
            onCancel: props.onUploadBankStatementButtonClick,
            onClose: props.onEditBankDetailsButtonClick,
          });
          break;
        case ValidationFailModalStateType.FAILED_ONE_TRY_LEFT:
          setModalProps({
            subtext:
              "We couldn't verify your bank account details. Please check the information and try again.",
            errorMessage: `${props.errorMessage}You have one attempt remaining`,
            confirmButtonLabel: 'Try again',
            cancelButtonLabel: 'Upload bank statement',
            onConfirm: props.onEditBankDetailsButtonClick,
            onCancel: props.onUploadBankStatementButtonClick,
            onClose: props.onEditBankDetailsButtonClick,
          });
          break;

        case ValidationFailModalStateType.FAILED_MAX_TRIES_EXCEEDED:
          setModalProps({
            subtext: 'Please upload a copy of your bank statement or proof of banking bellow.',
            errorMessage: `${props.errorMessage}Maximum number of attempts exceeded`,
            confirmButtonLabel: 'Upload bank statement',
            onConfirm: props.onUploadBankStatementButtonClick,
            onClose: props.onUploadBankStatementButtonClick!,
          });
          break;
        case ValidationFailModalStateType.FAILED_MAX_TRIES_EXCEEDED_FOR_RENTAL_ONLY:
          setModalProps({
            subtext: 'Bank account validation failed due to incorrect details provided.',
            errorMessage: `${props.errorMessage}Maximum number of attempts exceeded`,
            onClose: props.onClose,
          });
          break;
        case ValidationFailModalStateType.FAILED_WITH_RENTAL_PRODUCT:
          setModalProps({
            subtext:
              "We couldn't verify your bank account details. Please check the information and try again.",
            errorMessage: props.errorMessage,
            confirmButtonLabel: 'Try again',
            onConfirm: props.onTryAgain,
            onClose: props.onTryAgain,
          });
          break;
        case ValidationFailModalStateType.FAILED_ONE_TRY_LEFT_WITH_RENTAL_PRODUCT:
          setModalProps({
            subtext:
              "We couldn't verify your bank account details. Please check the information and try again.",
            errorMessage: `${props.errorMessage}You have one attempt remaining`,
            confirmButtonLabel: 'Try again',
            onConfirm: props.onTryAgain,
            onClose: props.onTryAgain,
          });
      }
    } else {
      switch (props.state) {
        case ValidationFailModalStateType.FAILED:
          setModalProps({
            subtext:
              'We are unable to verify your bank details. \nPlease enter the correct banking details to continue with your order.',
            confirmButtonLabel: 'Edit bank details',
            onConfirm: props.onEditBankDetailsButtonClick,
            onClose: props.onEditBankDetailsButtonClick,
          });
          break;
        case ValidationFailModalStateType.FAILED_ONE_TRY_LEFT:
          setModalProps({
            subtext:
              'We are unable to verify your bank details. \nPlease enter the correct banking details to continue with your order.',
            errorMessage: 'You have one final attempt remaining.',
            confirmButtonLabel: 'Edit bank details',
            onConfirm: props.onEditBankDetailsButtonClick,
            onClose: props.onEditBankDetailsButtonClick,
          });
          break;
        case ValidationFailModalStateType.FAILED_MAX_TRIES_EXCEEDED:
          setModalProps({
            subtext:
              ' Your application will be closed, if you want you can restart with the correct business and bank information.',
            errorMessage: `You have exceeded your ${props.maxNumberOfTriesWord} try limit.`,
            confirmButtonLabel: 'Restart application',
            onConfirm: props.onRestartApplication,
            onClose: props.onRestartApplication!,
          });
          break;
      }
    }
  }, [props]);

  return (
    <div className="ValidationFailModal">
      <ErrorModal
        visible={props.state !== ValidationFailModalStateType.HIDDEN}
        heading={'Unable to verify bank details'}
        showOR={
          (props.state === ValidationFailModalStateType.FAILED ||
            props.state === ValidationFailModalStateType.FAILED_ONE_TRY_LEFT) &&
          !bavIsDisabled
        }
        {...modalProps}
      />
    </div>
  );
};
